export type ReadingFontFamily = "system" | "serif" | "sans" | "mono";
export type ReadingTheme = "light" | "dark" | "sepia";

export interface ReadingPreferences {
  fontFamily: ReadingFontFamily;
  fontSize: string;
  theme: ReadingTheme;
}

export const READING_PREFERENCES_STORAGE_KEY = "pagebunnyReadingPreferences";
export const READING_PREFERENCES_UPDATED_EVENT = "readingPreferencesUpdated";

export const defaultReadingPreferences: ReadingPreferences = {
  fontFamily: "system",
  fontSize: "medium",
  theme: "light",
};

const validFontFamilies: ReadingFontFamily[] = [
  "system",
  "serif",
  "sans",
  "mono",
];
const validThemes: ReadingTheme[] = ["light", "dark", "sepia"];
const validFontSizes = ["small", "medium", "large", "extra-large"];

export const getReadingPreferences = (): ReadingPreferences => {
  if (typeof window === "undefined") {
    return defaultReadingPreferences;
  }

  try {
    const storedPreferences = window.localStorage.getItem(
      READING_PREFERENCES_STORAGE_KEY
    );

    if (!storedPreferences) {
      return defaultReadingPreferences;
    }

    const parsedPreferences = JSON.parse(storedPreferences) as Partial<
      ReadingPreferences
    >;
    const fontFamily = validFontFamilies.includes(
      parsedPreferences.fontFamily as ReadingFontFamily
    )
      ? (parsedPreferences.fontFamily as ReadingFontFamily)
      : defaultReadingPreferences.fontFamily;
    const theme = validThemes.includes(parsedPreferences.theme as ReadingTheme)
      ? (parsedPreferences.theme as ReadingTheme)
      : defaultReadingPreferences.theme;
    const fontSize =
      parsedPreferences.fontSize &&
      validFontSizes.includes(parsedPreferences.fontSize)
        ? parsedPreferences.fontSize
        : defaultReadingPreferences.fontSize;

    return {
      fontFamily,
      fontSize,
      theme,
    };
  } catch {
    return defaultReadingPreferences;
  }
};

export const saveReadingPreferences = (
  preferences: ReadingPreferences
): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    READING_PREFERENCES_STORAGE_KEY,
    JSON.stringify(preferences)
  );
  window.dispatchEvent(new Event(READING_PREFERENCES_UPDATED_EVENT));
};

export const getReadingFontFamilyStyle = (
  fontFamily: ReadingFontFamily
): string => {
  if (fontFamily === "serif") return "Georgia, 'Times New Roman', serif";
  if (fontFamily === "sans") return "Arial, Helvetica, sans-serif";
  if (fontFamily === "mono") {
    return "'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
  }

  return "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
};

export const getReadingFontSizeClass = (fontSize: string): string => {
  if (fontSize === "small") return "text-base";
  if (fontSize === "large") return "text-xl";
  if (fontSize === "extra-large") return "text-2xl";

  return "text-lg";
};
